import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar, ArrowRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default to handle submission manually
    setIsSubmitting(true);
    
    try {
      // Create form data
      const formDataToSend = new FormData();
      formDataToSend.append('form-name', 'contact');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('message', formData.message);
      
      // Submit to Netlify
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });
      
      if (response.ok) {
        // Success
        toast({
          title: "Message sent successfully!",
          description: "Thank you for your interest. I'll get back to you within 24 hours.",
        });
        setFormData({ name: "", email: "", website: "", message: "" });
      } else {
        // Error
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // Error handling
      toast({
        title: "Error sending message",
        description: "There was an error sending your message. Please try again or contact me directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center animate-on-scroll">
          <h2 className="mb-6">Let's Get Your Tracking Fixed — <span className="text-primary">Fast</span></h2>
          <p className="text-xl text-text-secondary mb-12 max-w-2xl mx-auto">
            Ready to transform your analytics from frustrating to reliable? 
            Let's discuss your specific tracking challenges and goals.
          </p>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start w-full">
            {/* CTA Section */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg animate-on-scroll w-full min-w-0">
              <div className="text-center mb-8">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold text-text-primary mb-4">
                  Book Your Free Strategy Call
                </h3>
                <p className="text-text-secondary mb-6">
                  15-minute consultation to understand your tracking needs and explore solutions.
                </p>
                <Button 
                  size="lg" 
                  onClick={() => window.open('https://calendly.com/jade-tagnetica/15-minute-strategy-call', '_blank')}
                  className="bg-primary hover:bg-primary/90 text-white px-4 sm:px-8 py-4 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group w-full"
                >
                  <span className="whitespace-normal">Book a Free Call</span>
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </div>
              
              <div className="border-t border-border-color pt-6">
                <p className="text-sm text-text-secondary text-center">
                  🔒 Your information is safe and secure<br/>
                  📞 No sales pressure, just helpful insights<br/>
                  ⚡ Get clarity on your tracking challenges<br/>
                  📅 Instant calendar booking
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg animate-on-scroll w-full min-w-0">
              <h3 className="text-2xl font-semibold text-text-primary mb-6 text-center">
                Or Send Us a Message
              </h3>
              
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true" 
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit} 
                className="space-y-6"
              >
                {/* Netlify hidden inputs */}
                <input type="hidden" name="form-name" value="contact" />
                <div className="hidden">
                  <input name="bot-field" />
                </div>
                
                <div>
                  <Label htmlFor="name" className="text-text-primary font-medium">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 border-border-color focus:border-primary w-full"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-text-primary font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-border-color focus:border-primary w-full"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <Label htmlFor="website" className="text-text-primary font-medium">
                    Website (Optional)
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="mt-2 border-border-color focus:border-primary w-full"
                    placeholder="https://your-website.com"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-text-primary font-medium">
                    Tell me about your tracking challenges
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="mt-2 border-border-color focus:border-primary resize-none w-full"
                    placeholder="Describe your current analytics setup and what you'd like to improve..."
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
